import { DAPStatusEnum } from '@ytl/common-model';
import { borgAnalytics, Button, getChannel, useTheme } from '@ytl/common-web';
import { useEffect, useRef, useState } from 'react';
import QRCode from 'react-qr-code';
import { ReactComponent as WarningIcon } from '../../../../assets/warning.svg';
import { useLanguage } from '../../../../contexts/languageContext';
import { OnboardingPhase, OnboardingStepperControls } from '../../../../modules/onboarding/OnboardingComponent';
import { WidgetEvent } from '../../../@types/widgetEvents';
import { getConfig } from '../../../data/config';
import { dapResource } from '../../../resource/dap.resource';
import { emitWidgetEvent } from '../../../utils/emitData';
import { DAPAppWaitingRoom } from './DAPAppWaitingRoom';

type DAPCodeProps = {
  stepper: OnboardingStepperControls;
  qrCodeValue?: string;
  sessionId: string | null;
  setSessionId: (sessionId: string | null) => void;
  backButtonText?: string;
  onBack?: () => void;
};

export const DAPCode = ({ stepper, qrCodeValue, sessionId, setSessionId, backButtonText, onBack }: DAPCodeProps) => {
  const { translation } = useLanguage();
  const [qrValue, setQrValue] = useState<string>(qrCodeValue || '');
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [, setEventStatus] = useState<string | null>(null);
  const [isInitialized, setIsInitialized] = useState<boolean>(false);
  const { isWebView } = useTheme();
  const [showQrCodeMobile, setShowQrCodeMobile] = useState<boolean>(false);

  // Ref to track if initialization has already been done (prevents double execution in StrictMode)
  const initializationRef = useRef<boolean>(false);

  useEffect(() => {
    // Prevent double execution in React StrictMode
    if (initializationRef.current) {
      return;
    }

    const fetchDapAuthorizeUrl = async () => {
      if (qrCodeValue) {
        setQrValue(qrCodeValue);
        setIsInitialized(true);
        return;
      }

      // Mark as initializing to prevent double execution
      initializationRef.current = true;

      try {
        setLoading(true);
        setError(null);

        // Generate a UUID that will be used as both correlation ID and external session ID
        const correlationId = crypto.randomUUID();
        console.log('[DAPCode] Generated correlation ID for DAP:', correlationId);

        // Call DAP resource to get authorize URL, using the same ID for both correlation and session
        const authorizeUrl = await dapResource.getAuthorizeUrl(correlationId, correlationId);
        console.log('[DAPCode] DAP Authorize URL:', authorizeUrl);
        if (isWebView) {
          window.location.href = `${getConfig().dapExternalUriBaseUrl}?uri=${encodeURIComponent(authorizeUrl)}`;
        } else {
          setQrValue(authorizeUrl);
        }

        // Only set sessionId after successful initialization
        setSessionId(correlationId);
        setIsInitialized(true);

        // Emit event that DÁP code step is shown
        emitWidgetEvent({
          eventName: WidgetEvent.DAPStatusReport,
          eventValue: { timestamp: Date.now(), authorizeUrl },
          isSuccess: true,
        });
      } catch (err) {
        console.error('Failed to fetch DAP authorize URL:', err);
        setError('Failed to load QR code');
        // Reset initialization ref on error so retry is possible
        initializationRef.current = false;
        emitWidgetEvent({
          eventName: WidgetEvent.DAPStatusReport,
          eventValue: { timestamp: Date.now(), error: err },
          isSuccess: false,
        });
        stepper.showError(DAPStatusEnum.UNEXPECTED_ERROR);
      } finally {
        setLoading(false);
      }
    };

    fetchDapAuthorizeUrl();
  }, [qrCodeValue, setSessionId, stepper, isWebView]);

  useEffect(() => {
    borgAnalytics.sendVirtualPageView({
      channel: getChannel(isWebView),
      virtualName: 'Dap authentication start',
    });
  }, [isWebView]);

  // Polling effect for event status - only start after initialization is complete
  useEffect(() => {
    // Only start polling after initialization is complete and we have a sessionId
    if (!sessionId || !isInitialized) return;

    const pollInterval = 3000; // Poll every 3 seconds
    const timeoutDuration = getConfig().dapTimeout || 300000; // 5 minutes default
    let intervalId: NodeJS.Timeout;
    let timeoutId: NodeJS.Timeout;
    let isCancelled = false;

    // Set up timeout to redirect to error page after configured duration
    timeoutId = setTimeout(() => {
      if (isCancelled) return;
      clearInterval(intervalId);
      emitWidgetEvent({
        eventName: WidgetEvent.DAPStatusReport,
        eventValue: { timestamp: Date.now(), error: 'TIMEOUT' },
        isSuccess: false,
      });
      stepper.showError(DAPStatusEnum.DAP_ERROR);
    }, timeoutDuration);

    const pollEventStatus = async () => {
      if (isCancelled) return;

      try {
        const status = await dapResource.getLastEventStatus(sessionId);

        if (isCancelled) return;

        setEventStatus(status);

        // Emit status update
        emitWidgetEvent({
          eventName: WidgetEvent.DAPStatusReport,
          eventValue: { timestamp: Date.now(), status, sessionId },
          isSuccess: true,
        });

        // Handle specific statuses if needed
        if (
          status?.toUpperCase() === DAPStatusEnum.SECOND_CONSENT_REQUIRED.toUpperCase() ||
          status?.toUpperCase() === DAPStatusEnum.DONE.toUpperCase()
        ) {
          // Could trigger automatic navigation or show success message
          console.log('DAP verification successful');
          stepper.goToNext();
        } else if (status?.toUpperCase() === DAPStatusEnum.FAILED.toUpperCase()) {
          clearInterval(intervalId);
          emitWidgetEvent({
            eventName: WidgetEvent.DAPStatusReport,
            eventValue: { timestamp: Date.now(), error: DAPStatusEnum.SECOND_CONSENT_REQUIRED },
            isSuccess: false,
          });
          stepper.showError(DAPStatusEnum.SECOND_CONSENT_REQUIRED);
        }
      } catch (err) {
        console.error('Failed to fetch event status:', err);
        clearInterval(intervalId);
        emitWidgetEvent({
          eventName: WidgetEvent.DAPStatusReport,
          eventValue: { timestamp: Date.now(), error: err },
          isSuccess: false,
        });
        stepper.showError(DAPStatusEnum.UNEXPECTED_ERROR);
      }
    };

    // Start polling
    pollEventStatus(); // Initial call
    intervalId = setInterval(pollEventStatus, pollInterval);

    // Cleanup on unmount
    return () => {
      isCancelled = true;
      if (intervalId) {
        clearInterval(intervalId);
      }
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [sessionId, isInitialized, stepper]);

  const handleBack = () => {
    if (onBack) {
      onBack();
    } else {
      stepper.goToPhase(OnboardingPhase.IdentifyMyself);
    }
  };

  const handleToDapAppClick = () => {
    if (qrValue) {
      window.location.href = qrValue;
    }
  };

  const ShowQRCode = () => {
    return (
      <div className="onboarding-step__info-box">
        <div className="onboarding-step__info-box-inner">
          {loading ? (
            <div>{translation.onboarding?.dapCode?.loading}</div>
          ) : error ? (
            <div className="onboarding-step__error">{error}</div>
          ) : qrValue ? (
            <QRCode
              value={qrValue}
              size={180}
              aria-label={translation.accessibility?.qrCode}
            />
          ) : null}
        </div>

        <p className="onboarding-step__info-box-text">{translation.onboarding?.dapCode?.instruction}</p>
      </div>
    );
  };

  return (
    <div className={`onboarding-step${isWebView ? ' onboarding-step--webview' : ''}`}>
      {isWebView ? (
        <DAPAppWaitingRoom />
      ) : (
        <>
          <h3 className="onboarding-step__header">{translation.onboarding?.dapCode?.header}</h3>

          {/* Yellow warning box */}
          <div className="onboarding-step__warning">
            <WarningIcon className="onboarding-step__warning-icon" />
            <div className="onboarding-step__warning-content">
              <strong>{translation.onboarding?.dapCode?.warningTitle}</strong>
              <p>{translation.onboarding?.dapCode?.warningText}</p>
            </div>
          </div>

          <div className="hide-in-desktop-view">
            <div className={`onboarding-step__actions${isWebView ? ' onboarding-step__actions--webview' : ''}`}>
              <Button
                variant="contained"
                onClick={handleToDapAppClick}
              >
                {translation.onboarding?.dapCode?.toDapApp}
              </Button>
              <div className="register-page__container__divider">
                <span>{translation.loginRegister.register.box.oauthSectionLabel}</span>
              </div>
              {showQrCodeMobile ? (
                <ShowQRCode />
              ) : (
                <Button
                  variant="outlined"
                  onClick={() => setShowQrCodeMobile(true)}
                >
                  {translation.onboarding?.dapCode?.showQRCode}
                </Button>
              )}
            </div>
          </div>

          <div className="hide-in-mobile-view">
            <ShowQRCode />
          </div>
        </>
      )}
      {/* Back button */}
      <div className={`onboarding-step__back${isWebView ? ' onboarding-step__back--webview' : ''}`}>
        <Button
          variant="text"
          onClick={handleBack}
        >
          {backButtonText ||
            (isWebView
              ? translation.yeppRegistration?.webcomponent.back
              : translation.onboarding?.dapCode?.backToLogin)}
        </Button>
      </div>
    </div>
  );
};
